import { FC, useEffect, useMemo, useState } from 'react';
import { format, isAfter } from 'date-fns';
import { Control, useForm, Validate } from 'react-hook-form';

import { parseValidEnumeration } from '@/components/search/v1/utils/searchValidations';
import {
  type ConflictenFilter,
  defaultFilter,
} from '@/components/sidebar/conflicten/conflictenFilter/conflictenFilterModel';
import SortField from '@/components/sidebar/sortField/SortField';
import {
  getSortConflictenOptions,
  resolveSorting,
  SorteringRichting,
  type SorteringType,
  useSorting,
} from '@/components/sidebar/sortField/useSorting';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { CollapsibleContent } from '@/components/UI/Collapsible/Collapsible';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { FormErrorNotification } from '@/components/UI/Fields/FormErrorNotification/FormErrorNotification';
import { InputField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import DienstregelpuntSelectField from '@/components/UI/Fields/ReactFormFields/DienstregelpuntSelectField';
import MaterieelTypeSelectField from '@/components/UI/Fields/ReactFormFields/MaterieelTypeSelectField';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { Label } from '@/components/UI/Label/Label';
import useDienstregelpunten from '@/state/selectors/useDienstregelpunten';
import type { ConflictType } from '@/types/Conflicten';
import type { LabeledEntryOrString } from '@/types/LabeledEntry';
import { constructZuluTimeString } from '@/utils/dateUtils';
import { getDateVariations } from '@/utils/dateVariations';

import styles from './ConflictenFilterDialog.module.css';
import inputFieldStyles from '@/components/UI/Fields/Fields.module.css';

export { defaultFilter };
export type { ConflictenFilter };

const sortConflictenOptions = getSortConflictenOptions();
const defaultSortType = sortConflictenOptions[0];
const defaultValueSortDirection = SorteringRichting.UP;

const unmarshallFormData = (input: LabeledEntryOrString[]) => {
  if (input) {
    return input.map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      return item.value;
    });
  }
  return [];
};

const eenheidOfTreinNummerRegex = /^[\d]{3,14}$/; // NOSONAR
const eenheidOfTreinReeksRegex = /^([\d]{1,14}-)+[\d]{1,14}$/; // NOSONAR

const valideerEenheidOfTreinnummer: (value: string) => boolean | string = (value) => {
  return value === '' ||
    eenheidOfTreinNummerRegex.test(value) ||
    eenheidOfTreinReeksRegex.test(value) ||
    parseValidEnumeration(value)
    ? true
    : 'Invoer is geen geldige opsomming of reeks';
};

const TypeCheckboxItem: FC<{ testId: string; label: string; onChange: (checked: boolean) => void; value: boolean }> = ({
  testId,
  label,
  onChange,
  value,
}) => (
  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
    <Checkbox data-testid={testId} onChange={onChange} value={value} />
    <span className={styles.conflictTypeStyle} onClick={() => onChange(!value)}>
      {label}
    </span>
  </div>
);

export type ConflictenFilterDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  setNieuwFilter: (filter: ConflictenFilter) => void;
  huidigFilter: ConflictenFilter;
};

export type FilterConflictenFormProps = {
  eenheidOfTreinnummer: string;
  dienstregelpunten: LabeledEntryOrString[] | null;
  materieelTypen: LabeledEntryOrString[] | null;
  sorteringType: SorteringType;
  sorteringRichting: SorteringRichting;
  datumVanDag: string;
  datumVanTijd: string;
  datumTotEnMetDag: string;
  datumTotEnMetTijd: string;
};

export const ConflictenFilterDialog = ({
  showDialog,
  handleOpenChange,
  huidigFilter,
  setNieuwFilter,
}: ConflictenFilterDialogProps) => {
  const lijstVanDienstregelpunten = useDienstregelpunten();
  const { handleSubmit, reset, control, setValue, getValues } = useForm<FilterConflictenFormProps>({
    mode: 'onSubmit',
  });

  const { sorteringType: initialValueSortType, sorteringRichting: initialValueSortDirection } = useMemo(
    () => resolveSorting(huidigFilter, defaultFilter),
    [huidigFilter.sorteringRichting, huidigFilter.sorteringType],
  );

  const {
    handleSortChange,
    handleSortDirectionChange,
    selectedSortType,
    selectedSortDirection,
    setSelectedSortDirection,
    setSelectedSortType,
  } = useSorting(initialValueSortType, initialValueSortDirection);

  useEffect(() => {
    if (selectedSortDirection) {
      setValue('sorteringRichting', selectedSortDirection);
    }
  }, [selectedSortDirection, setValue]);

  useEffect(() => {
    if (selectedSortType) {
      setValue('sorteringType', selectedSortType);
    }
  }, [selectedSortType, setValue]);

  const createOption = (label: string) => ({ label, value: label });

  const initialValueConflictTypen = useMemo(() => huidigFilter.conflictTypen ?? [], [huidigFilter.conflictTypen]);
  const [typeCheckboxes, setTypeCheckboxes] = useState(initialValueConflictTypen);
  const [incorrectDate, setIncorrectDate] = useState<boolean>();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  // Wanneer verwijderCriteria worden geclick moet datumFilter niet worden gebruiken
  const [datumFilter, setDatumFilter] = useState(true);
  const dateVariations = getDateVariations().slice(0, 3);

  const handleTypeChange = (type: ConflictType, checked: boolean) => {
    setTypeCheckboxes((current) => (checked ? [...current, type] : current.filter((v) => v !== type)));
  };

  const handleDatumVanDagChange = (newValue) => {
    setValue('datumVanDag', newValue);

    const datumTotEnMetDag = getValues('datumTotEnMetDag');

    if (isAfter(new Date(newValue), new Date(datumTotEnMetDag))) {
      if (newValue === dateVariations[1].value) {
        // Morgen geselecteerd voor van datum, verander tot datum naar overmorgen
        setValue('datumTotEnMetDag', dateVariations[2].value);
        setValue('datumVanTijd', '02:00');
      }
    }
  };

  const handleDatumTotEnMetDagChange = (newValue) => {
    setValue('datumTotEnMetDag', newValue);

    const datumVanDag = getValues('datumVanDag');

    if (isAfter(new Date(datumVanDag), new Date(newValue))) {
      // Wanneer totDatum voor vanDatum liggen, verander vanDatum naar vandaag
      setValue('datumVanTijd', format(new Date(), 'HH:mm'));
      setValue('datumVanDag', dateVariations[0].value);
    }
  };

  const onSubmit = async (formData: FilterConflictenFormProps) => {
    let datumVan;
    let datumTotEnMet;
    if (datumFilter) {
      datumVan = constructZuluTimeString(formData.datumVanDag, formData.datumVanTijd);
      datumTotEnMet = constructZuluTimeString(formData.datumTotEnMetDag, formData.datumTotEnMetTijd);
    }
    const kandidaatFilter = {
      eenheidOfTreinnummer: formData.eenheidOfTreinnummer,
      dienstregelpunten:
        formData.dienstregelpunten && formData.dienstregelpunten.length != 0
          ? unmarshallFormData(formData.dienstregelpunten)
          : undefined,
      materieelTypen:
        formData.materieelTypen && formData.materieelTypen.length != 0
          ? unmarshallFormData(formData.materieelTypen)
          : undefined,
      conflictTypen: typeCheckboxes.length === 0 ? undefined : typeCheckboxes,
      sorteringRichting: formData.sorteringRichting,
      sorteringType: formData.sorteringType,
      datumVan,
      datumTotEnMet,
    };

    // Check if the selectedPeriode is correct, iow Van is not later than TotEnMet
    if (isAfter(new Date(kandidaatFilter.datumVan), new Date(kandidaatFilter.datumTotEnMet))) {
      setIncorrectDate(true);
      setSubmitDisabled(true);
    } else {
      setIncorrectDate(false);
      setSubmitDisabled(false);
      if (
        Object.values(kandidaatFilter).every(
          (el) => el === undefined || el === '' || el === defaultValueSortDirection || el === defaultSortType.value,
        )
      ) {
        setNieuwFilter(defaultFilter);
      } else {
        setNieuwFilter(kandidaatFilter);
      }

      setTimeout(() => {
        handleOpenChange(false);
      }, 100);
    }
  };

  return (
    <MwdDialog
      title='Filter conflicten'
      className={styles.conflictenFilterenDialogStyle}
      hasError={submitDisabled}
      resetErrorState={() => setSubmitDisabled(false)}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid='filterConflicten'
      hasCloseInHeader
      headerSeparator
      paddingTopChildren={false}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SortField
          name='sorteringType'
          control={control}
          options={sortConflictenOptions}
          defaultValue={huidigFilter.sorteringType ?? defaultSortType.label}
          onChangeCallback={(value) => handleSortChange(value as SorteringType)}
          onSortDirectionChangeCallback={handleSortDirectionChange}
          selectedSortDirection={selectedSortDirection}
        />
        <div style={{ paddingBottom: '16px' }}>
          <InputField
            className={`${inputFieldStyles.inputField} ${styles.eenheidOfTreinnummerFilterInputStyle}`}
            label='Eenheid- of treinnummer'
            control={control}
            autoComplete='off'
            data-testid='eenheidOfTreinnummer'
            placeholder='Filter op nummer(s) of reeks'
            maxLength={255}
            name='eenheidOfTreinnummer'
            rules={{
              validate: {
                eenheidOfTreinnummer: valideerEenheidOfTreinnummer as Validate<
                  LabeledEntryOrString | LabeledEntryOrString[] | null,
                  FilterConflictenFormProps
                >,
              },
            }}
            showOptional={false}
            defaultValue={huidigFilter.eenheidOfTreinnummer ?? ''}
          />
        </div>
        <div>
          <DienstregelpuntSelectField
            control={control as Control<any>}
            name='dienstregelpunten'
            defaultValue={
              huidigFilter.dienstregelpunten ? huidigFilter.dienstregelpunten.map((item) => createOption(item)) : []
            }
            alleDrglptnamen={lijstVanDienstregelpunten}
          />
        </div>
        <MaterieelTypeSelectField
          name='materieelTypen'
          control={control as Control<any>}
          defaultValue={
            huidigFilter.materieelTypen ? huidigFilter.materieelTypen.map((item) => createOption(item)) : []
          }
        />
        <div>
          <Label>
            <div
              style={{ paddingTop: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}
            >
              Conflicttype
            </div>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <TypeCheckboxItem
              label='Manco'
              testId='type-manco'
              onChange={(isChecked) => handleTypeChange('MANCO', isChecked)}
              value={typeCheckboxes.includes('MANCO')}
            />
            <TypeCheckboxItem
              label='Plaats'
              testId='type-plaats'
              onChange={(isChecked) => handleTypeChange('PLAATS', isChecked)}
              value={typeCheckboxes.includes('PLAATS')}
            />
            <TypeCheckboxItem
              label='Tijd'
              testId='type-tijd'
              onChange={(isChecked) => handleTypeChange('TIJD', isChecked)}
              value={typeCheckboxes.includes('TIJD')}
            />
            <TypeCheckboxItem
              label='Versterkt'
              testId='type-versterkt'
              onChange={(isChecked) => handleTypeChange('VERSTERKT_MATERIEEL', isChecked)}
              value={typeCheckboxes.includes('VERSTERKT_MATERIEEL')}
            />
            <TypeCheckboxItem
              label='Verkeerd materieel'
              testId='type-verkeerdmat'
              onChange={(isChecked) => handleTypeChange('VERKEERD_MATERIEEL', isChecked)}
              value={typeCheckboxes.includes('VERKEERD_MATERIEEL')}
            />
            <TypeCheckboxItem
              label='Perronlengte'
              testId='type-perronlengte'
              onChange={(isChecked) => handleTypeChange('PERRONLENGTE', isChecked)}
              value={typeCheckboxes.includes('PERRONLENGTE')}
            />
            <TypeCheckboxItem
              label='Koppel'
              testId='type-koppel'
              onChange={(isChecked) => handleTypeChange('KOPPEL', isChecked)}
              value={typeCheckboxes.includes('KOPPEL')}
            />
            <TypeCheckboxItem
              label='Geaccepteerd'
              testId='type-geaccepteerd'
              onChange={(isChecked) => handleTypeChange('GEACCEPTEERD', isChecked)}
              value={typeCheckboxes.includes('GEACCEPTEERD')}
            />
          </div>
        </div>
        <div
          style={{
            marginBottom: '64px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <SelectField
            shortInputField
            testId='datumVanDag'
            minWidth='120px'
            control={control}
            defaultValue={dateVariations[0].value}
            name='datumVanDag'
            label='Van dag'
            // Alleen vandaag en morgen is van opties
            options={dateVariations}
            onChangeCallback={(value) => handleDatumVanDagChange(value)}
          />
          <InputField
            geavanceerdZoekenVariant
            // minHeight='120px'
            control={control}
            data-testid='datumVanTijd'
            type='time'
            role='input'
            required
            width='90px'
            defaultValue={format(new Date(), 'HH:mm')}
            name='datumVanTijd'
            label='Van tijd'
            // style={{ marginTop: '4px', minHeight: '52px', display: 'flex', position: 'relative' }}
          />
          <div
            style={{
              display: 'flex',
              alignSelf: 'center',
              marginTop: '20px',
              color: 'var(--tractie-color-content-default)',
            }}
          >
            {' '}
            -{' '}
          </div>
          <SelectField
            shortInputField
            minWidth='120px'
            testId='datumTotEnMetDag'
            control={control}
            name='datumTotEnMetDag'
            label='Tot dag'
            defaultValue={dateVariations[2].value}
            options={dateVariations}
            onChangeCallback={(value) => handleDatumTotEnMetDagChange(value)}
          />
          <InputField
            geavanceerdZoekenVariant
            control={control}
            defaultValue='02:00'
            data-testid='datumTotEnMetTijd'
            type='time'
            role='input'
            required
            width='90px'
            name='datumTotEnMetTijd'
            label='Tot tijd'
          />
        </div>
        <CollapsibleContent open={submitDisabled} onOpenChange={setSubmitDisabled}>
          {incorrectDate && submitDisabled ? (
            <FormErrorNotification errorMessage='De eindtijd moet na de starttijd liggen' />
          ) : null}
        </CollapsibleContent>
        <MwdDialog.Footer
          submitCaption='Filter conflicten'
          cancelCallback={() => {
            reset({
              eenheidOfTreinnummer: '',
              dienstregelpunten: null,
              materieelTypen: null,
              sorteringType: defaultSortType.value,
              sorteringRichting: defaultValueSortDirection,
            });
            setSelectedSortType(defaultSortType.value);
            setSelectedSortDirection(defaultValueSortDirection);
            setTypeCheckboxes([]);
            setDatumFilter(false);
            setNieuwFilter(defaultFilter);
            handleOpenChange(false);
          }}
          cancelCaption='Verwijder criteria'
          justifyContentStyle='space-between'
        />
      </form>
    </MwdDialog>
  );
};
